import { CALLOUT_DATA, CALLOUT_GRID } from "./callouts.generated.ts";
import type { Level, MapId } from "./catalog.ts";
import { levelAt, radarToWorld, worldToRadar } from "./coords.ts";
import { areaAt, type RadarBox, type ZoneArea } from "./zones.ts";

/** A callout's name and where to write it on the radar. */
export interface CalloutLabel {
  name: string;
  u: number;
  v: number;
  /** How big the callout is (grid cells): bigger ones win when labels overlap. */
  size: number;
}

/**
 * A change an admin made to a map's callouts, stored by the API and applied on top
 * of Valve's places:
 * - `base` set: one of Valve's labels (`base` = its place name, `ordinal` = which of
 *   that place's labels, 0 for the first), renamed, moved, given another area or hidden.
 * - `base` null: a callout of 69LY's own, covering `zone`.
 */
export interface CalloutEdit {
  level: Level;
  base: string | null;
  ordinal: number;
  name: string;
  u: number;
  v: number;
  /** Null: the area worked out from the map. */
  area: ZoneArea | null;
  hidden: boolean;
  zone: RadarBox | null;
}

/** One callout on a map floor, after the admins' changes. */
export interface Callout extends CalloutLabel {
  /** Stable within a map floor: `base:<place>#<ordinal>` or `own:<index>`. */
  key: string;
  base: string | null;
  ordinal: number;
  area: ZoneArea;
  /** The area from the map alone, before any change. */
  defaultArea: ZoneArea;
  hidden: boolean;
  zone: RadarBox | null;
  edited: boolean;
}

const grids = new Map<string, Uint8Array>();

function grid(map: MapId, level: Level): Uint8Array | null {
  const key = `${map}:${level}`;
  const cached = grids.get(key);
  if (cached) return cached;
  const data = CALLOUT_DATA[map]?.levels[level];
  if (!data) return null;
  const out = new Uint8Array(CALLOUT_GRID * CALLOUT_GRID);
  let i = 0;
  for (const run of data.grid.split(";")) {
    if (!run) continue;
    const [v, n] = run.split(",").map((x) => parseInt(x, 36));
    out.fill(v, i, i + n);
    i += n;
  }
  grids.set(key, out);
  return out;
}

/** Areas that do not follow from where the label sits: spawns, mids, and places the site anchors get wrong. */
const NAME_AREAS: Record<string, ZoneArea> = {
  "A Site": "a",
  "B Site": "b",
  "CT Spawn": "other",
  "T Spawn": "other",
  Mid: "mid",
  "Top of Mid": "mid",
  "Lower Mid": "mid",
  "Second Mid": "mid",
};
const MAP_AREAS: Partial<Record<MapId, Record<string, ZoneArea>>> = {
  de_mirage: { "Tunnel Stairs": "mid", House: "other", "Side Alley": "other" },
  de_dust2: { "Outside Long": "a", "T Ramp": "other" },
  de_inferno: { Library: "a", Apartments: "a", "Back Alley": "mid" },
  de_nuke: { Ramp: "b", Secret: "b", Squeaky: "a", Silo: "other", Mini: "other" },
};

function defaultArea(map: MapId, level: Level, name: string, u: number, v: number): ZoneArea {
  const known = MAP_AREAS[map]?.[name] ?? NAME_AREAS[name];
  if (known) return known;
  const w = radarToWorld(map, { u, v });
  return areaAt(map, w.x, w.y, level === "lower" ? -10_000 : null);
}

const editKey = (e: Pick<CalloutEdit, "level" | "base" | "ordinal">) => `${e.level}:${e.base}#${e.ordinal}`;

interface BaseLabel {
  base: string;
  ordinal: number;
  u: number;
  v: number;
  size: number;
  area: ZoneArea;
}
const baseCache = new Map<string, BaseLabel[]>();

/** Valve's labels on a map floor with their default areas, worked out once. */
function baseLabels(map: MapId, level: Level): BaseLabel[] {
  const key = `${map}:${level}`;
  const cached = baseCache.get(key);
  if (cached) return cached;
  const data = CALLOUT_DATA[map];
  const seen = new Map<string, number>();
  const out = (data?.levels[level]?.labels ?? []).map((l) => {
    const base = data.names[l.place];
    const ordinal = seen.get(base) ?? 0;
    seen.set(base, ordinal + 1);
    return { base, ordinal, u: l.u, v: l.v, size: l.size, area: defaultArea(map, level, base, l.u, l.v) };
  });
  baseCache.set(key, out);
  return out;
}

/**
 * Every callout on a map floor, Valve's then 69LY's own, with the admins' changes
 * applied. Hidden ones are included (the admin page shows them).
 */
export function callouts(map: MapId, level: Level = "upper", edits: readonly CalloutEdit[] = []): Callout[] {
  const byKey = new Map(edits.filter((e) => e.base !== null).map((e) => [editKey(e), e]));
  const out: Callout[] = [];
  for (const l of baseLabels(map, level)) {
    const { base, ordinal, area: def } = l;
    const e = byKey.get(editKey({ level, base, ordinal }));
    out.push({
      key: `base:${base}#${ordinal}`,
      base,
      ordinal,
      name: e?.name || base,
      u: e?.u ?? l.u,
      v: e?.v ?? l.v,
      size: l.size,
      area: e?.area ?? def,
      defaultArea: def,
      hidden: e?.hidden ?? false,
      zone: e?.zone ?? null,
      edited: !!e,
    });
  }
  edits
    .filter((e) => e.base === null && e.level === level)
    .forEach((e, i) => {
      const z = e.zone ?? { u0: e.u - 0.01, v0: e.v - 0.01, u1: e.u + 0.01, v1: e.v + 0.01 };
      const cells = Math.max(1, Math.round((z.u1 - z.u0) * (z.v1 - z.v0) * CALLOUT_GRID * CALLOUT_GRID));
      const w = radarToWorld(map, { u: (z.u0 + z.u1) / 2, v: (z.v0 + z.v1) / 2 });
      const def = areaAt(map, w.x, w.y, level === "lower" ? -10_000 : null);
      out.push({ key: `own:${i}`, base: null, ordinal: 0, name: e.name, u: e.u, v: e.v, size: cells, area: e.area ?? def, defaultArea: def, hidden: e.hidden, zone: z, edited: true });
    });
  return out;
}

/** The callout names to write on a map floor, after the admins' changes. */
export function calloutLabels(map: MapId, level: Level = "upper", edits: readonly CalloutEdit[] = []): CalloutLabel[] {
  return callouts(map, level, edits)
    .filter((c) => !c.hidden)
    .map(({ name, u, v, size }) => ({ name, u, v, size }));
}

const inBox = (b: RadarBox, u: number, v: number) => u >= b.u0 && u <= b.u1 && v >= b.v0 && v <= b.v1;
const boxArea = (b: RadarBox) => (b.u1 - b.u0) * (b.v1 - b.v0);

/**
 * The callout a world spot is in. 69LY's own callouts (drawn boxes) come first, the
 * smallest box winning; then Valve's places, a spot just off the walkable floor (a
 * smoke on a wall) taking the nearest one. A place with several labels answers with
 * the label nearest to the spot.
 */
export function calloutInfoAt(map: MapId, x: number, y: number, z: number | null = null, edits: readonly CalloutEdit[] = []): Callout | null {
  const level = levelAt(map, z);
  const p = worldToRadar(map, x, y);
  const all = callouts(map, level, edits);
  const boxed = all.filter((c) => c.zone && !c.hidden && inBox(c.zone, p.u, p.v)).sort((a, b) => boxArea(a.zone!) - boxArea(b.zone!));
  if (boxed.length) return boxed[0];

  const data = CALLOUT_DATA[map];
  const g = grid(map, level);
  if (!data || !g) return null;
  const cx = Math.floor(p.u * CALLOUT_GRID);
  const cy = Math.floor(p.v * CALLOUT_GRID);
  for (let r = 0; r <= 3; r++) {
    let best = 0;
    let bestD = Infinity;
    for (let dy = -r; dy <= r; dy++)
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x2 = cx + dx;
        const y2 = cy + dy;
        if (x2 < 0 || y2 < 0 || x2 >= CALLOUT_GRID || y2 >= CALLOUT_GRID) continue;
        const v = g[y2 * CALLOUT_GRID + x2];
        const d = dx * dx + dy * dy;
        if (v && d < bestD) {
          best = v;
          bestD = d;
        }
      }
    if (best) {
      const place = data.names[best - 1];
      const labels = all.filter((c) => c.base === place);
      if (labels.length === 0) return null;
      // Distance to where Valve put the label, not where an admin moved it.
      const near = labels.length === 1 ? labels[0] : labels.sort((a, b) => dist(map, level, a, p) - dist(map, level, b, p))[0];
      return near.hidden ? null : near;
    }
  }
  return null;
}

function dist(map: MapId, level: Level, c: Callout, p: { u: number; v: number }) {
  const l = baseLabels(map, level).find((x) => x.base === c.base && x.ordinal === c.ordinal);
  return ((l?.u ?? c.u) - p.u) ** 2 + ((l?.v ?? c.v) - p.v) ** 2;
}

/** The callout a world spot is in, as players call it ("Jungle", "Top of Mid"). */
export function calloutAt(map: MapId, x: number, y: number, z: number | null = null, edits: readonly CalloutEdit[] = []): string | null {
  return calloutInfoAt(map, x, y, z, edits)?.name ?? null;
}

/** The area a spot belongs to: its callout's area, else the map's site and mid anchors. */
export function areaOfSpot(map: MapId, x: number, y: number, z: number | null = null, edits: readonly CalloutEdit[] = []): ZoneArea {
  return calloutInfoAt(map, x, y, z, edits)?.area ?? areaAt(map, x, y, z);
}

/** The size of the grid callouts are stored on (cells per side of the radar). */
export const CALLOUT_CELLS = CALLOUT_GRID;

/**
 * Which grid cells of a map floor a callout covers (1 = covered), CALLOUT_CELLS²
 * cells row by row: Valve's place cells nearest to this label, or the drawn box.
 */
export function calloutMask(map: MapId, level: Level, key: string, edits: readonly CalloutEdit[] = []): Uint8Array {
  const out = new Uint8Array(CALLOUT_GRID * CALLOUT_GRID);
  const all = callouts(map, level, edits);
  const c = all.find((x) => x.key === key);
  if (!c) return out;
  if (c.zone) {
    for (let y = Math.floor(c.zone.v0 * CALLOUT_GRID); y < Math.ceil(c.zone.v1 * CALLOUT_GRID); y++)
      for (let x = Math.floor(c.zone.u0 * CALLOUT_GRID); x < Math.ceil(c.zone.u1 * CALLOUT_GRID); x++)
        if (x >= 0 && y >= 0 && x < CALLOUT_GRID && y < CALLOUT_GRID) out[y * CALLOUT_GRID + x] = 1;
    if (c.base === null) return out;
  }
  const data = CALLOUT_DATA[map];
  const g = grid(map, level);
  if (!data || !g || c.base === null) return out;
  const place = data.names.indexOf(c.base) + 1;
  const labels = baseLabels(map, level).filter((l) => l.base === c.base);
  for (let i = 0; i < g.length; i++) {
    if (g[i] !== place) continue;
    if (labels.length > 1) {
      const u = ((i % CALLOUT_GRID) + 0.5) / CALLOUT_GRID;
      const v = (Math.floor(i / CALLOUT_GRID) + 0.5) / CALLOUT_GRID;
      const near = labels.reduce((a, b) => ((a.u - u) ** 2 + (a.v - v) ** 2 <= (b.u - u) ** 2 + (b.v - v) ** 2 ? a : b));
      if (near.ordinal !== c.ordinal) continue;
    }
    out[i] = 1;
  }
  return out;
}
